import React, { useState, useEffect } from 'react';
import { Image, Spin } from 'antd';
import Spinner from '../../Spinner';
import { CloseOutlined, FileOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useHeicConverter } from '../../../hooks/useHeicConverter';

const PreviewImageWithHeicSupport = ({ src, ...props }) => {
  const { loadAndConvertFromUrl } = useHeicConverter();
  const [finalSrc, setFinalSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const loadImage = async () => {
      try {
        if (src?.toLowerCase().endsWith(".heic") || src?.toLowerCase().endsWith(".heif")) {
          const convertedUrl = await loadAndConvertFromUrl(src);
          if (isMounted) setFinalSrc(convertedUrl);
        } else {
          setFinalSrc(src);
        }
      } catch (err) {
        console.error("Image load failed", err);
        setFinalSrc(src);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadImage();
    return () => { isMounted = false; };
  }, [src]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[96px]">
        <Spinner />
      </div>
    );
  }
  return <Image src={finalSrc} {...props} />;
};

export const PinAttachmentPreview = ({ attachment, isEditing, onRemove }) => {
  const url = attachment.photo_url || attachment.url;
  const thumbnail = attachment.thumbnail;
  const fileTypeRaw = attachment.fileType || attachment.type || '';
  const fileType = fileTypeRaw.toLowerCase();

  const isImage = fileType.includes('image') || /\.(jpg|jpeg|png|gif|webp|heic|heif)(\?.*)?$/i.test(url);
  const isVideo = fileType.includes('video') || fileType === 'mp4' || /\.(mp4|mov|avi|wmv|flv|mkv)(\?.*)?$/i.test(url);

  const containerStyle = {
    width: '100%',
    height: isEditing ? 80 : 96,
    borderRadius: 6,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: '#f1f1f1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e0e0e0'
  };

  const handleOpen = () => {
    window.open(url, '_blank');
  };

  const renderPreview = () => {
    if (isImage) {
      return (
        <PreviewImageWithHeicSupport
          src={url}
          width='100%'
          height={isEditing ? 80 : 96}
          style={{ objectFit: 'cover' }}
        />
      );
    }

    if (isVideo) {
      if (thumbnail) {
        return (
          <div className='relative w-full h-full cursor-pointer' onClick={handleOpen}>
            <Image
              src={thumbnail}
              width='100%'
              height='100%'
              style={{ objectFit: 'cover' }}
              preview={false}
            />
            <div className='absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/40 transition-colors'>
              <PlayCircleOutlined style={{ fontSize: 24, color: '#fff' }} />
            </div>
          </div>
        );
      }
      return (
        <div className='flex flex-col items-center justify-center w-full h-full cursor-pointer bg-blue-50 hover:bg-blue-100 transition-colors' onClick={handleOpen}>
          <FileOutlined style={{ fontSize: 32, color: '#1890ff' }} />
          <span className='text-[10px] mt-1 text-blue-600 font-medium uppercase'>Video</span>
        </div>
      );
    }

    return (
      <div className='flex flex-col items-center justify-center w-full h-full cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors' onClick={handleOpen}>
        <FileOutlined style={{ fontSize: 32, color: '#8c8c8c' }} />
        <span className='text-[10px] mt-1 text-gray-500 font-medium uppercase'>
          {fileTypeRaw.includes('/') ? fileTypeRaw.split('/')[1] : (fileTypeRaw || 'File')}
        </span>
      </div>
    );
  };

  return (
    <div style={containerStyle} className='group'>
      {renderPreview()}
      {isEditing && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(attachment.id);
          }}
          className='absolute top-1 right-1 border-1 border-red-500 text-[#fff] rounded-full w-4 h-4 text-xs flex items-center justify-center cursor-pointer bg-red-500 transition-opacity'>
          <CloseOutlined />
        </button>
      )}
    </div>
  );
};
